import type { PublicKey } from "@solana/web3.js"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import { useWallet } from "@solana/wallet-adapter-react"

import { WORMHOLE_SLUGS } from "@/governance/components/wormhole/constants"
import type { Organization } from "@/query/graphql"
import { Chain, useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import { useMultigovStaking } from "@/web3/solana/wormhole-staking/useMultigovStaking"

export const useStakeAccountWarning = ({
  organization,
}: {
  organization?: Pick<Organization, "slug">
}) => {
  const router = useRouter()
  const { multichainAccount } = useMultichainAccount()
  const { getStakeMetadataAddress } = useMultigovStaking()
  const { publicKey } = useWallet()
  const [stakeAccount, setStakeAccount] = useState<PublicKey | null>(null)
  const [stakeAccountFetched, setStakeAccountFetched] = useState(false)

  const { chain } = multichainAccount

  const checkAccount = useCallback(async () => {
    const accountData = await getStakeMetadataAddress(publicKey as PublicKey)

    if (accountData) {
      setStakeAccount(accountData)
    } else {
      setStakeAccount(null)
    }

    setStakeAccountFetched(true)
  }, [getStakeMetadataAddress, publicKey])

  const showStakeAccountWarning = useMemo(() => {
    return (
      chain === Chain.SOL &&
      !stakeAccount &&
      ((organization?.slug && WORMHOLE_SLUGS.includes(organization?.slug)) ||
        router.pathname === "/solana")
    )
  }, [chain, organization?.slug, router.pathname, stakeAccount])

  useEffect(() => {
    if (!publicKey || !showStakeAccountWarning) {
      return
    }

    checkAccount()
  }, [
    publicKey,
    getStakeMetadataAddress,
    showStakeAccountWarning,
    stakeAccount,
    checkAccount,
  ])

  return {
    showStakeAccountWarning,
    stakeAccountFetched,
    stakeAccount,
  }
}
